import type { WorkOrderChangeResultData, WorkOrderChangeSeasonData } from 'gql/graphql';

import { useMemo } from 'react';

import { getFindKey, getSeasonAccessor } from '../../../WorkOrder/WorkOrderSummaryTable/helpers';
import type { OrderSummaryTableRow } from '../../../WorkOrder/WorkOrderSummaryTable/hooks/useOrderSummaryTableRows';
import {
    chapterOrSectionTitle,
    chapterSectionsSequence,
    OrderSummaryTableRowType,
} from '../../../WorkOrder/WorkOrderSummaryTable/hooks/useOrderSummaryTableRows';

export const useOrderChangeSummaryTableRows = (
    data: WorkOrderChangeResultData[],
    seasons: WorkOrderChangeSeasonData[],
) =>
    useMemo(() => {
        const rows: OrderSummaryTableRow[] = [];
        const dataByChaptersOrSequences = chapterSectionsSequence.map((key) => ({
            key,
            items: data?.filter((s) => getFindKey(s) === key),
        }));

        let groupsAvailable = 0;

        dataByChaptersOrSequences.forEach((chOrSData) => {
            if (chOrSData.items?.length) {
                const groupTitle = chapterOrSectionTitle[chOrSData.key];
                const { items } = chOrSData;

                groupsAvailable += 1;
                rows.push({ title: groupTitle, type: OrderSummaryTableRowType.SUBHEADER });

                items.forEach((item) => {
                    const { title, totalPlanCost, totalCompletedCost, totalRemainderCost, seasonResults } = item;
                    const dataRow: OrderSummaryTableRow = {
                        title,
                        type: OrderSummaryTableRowType.DATA,
                        totalPlanCost,
                        totalCompletedCost,
                        totalRemainderCost,
                    };

                    seasons.forEach((season) => {
                        const { id } = season;
                        const seasonData = seasonResults.find((d) => d.seasonId === id);

                        if (seasonData) {
                            const seasonColumnKey = getSeasonAccessor(id);

                            dataRow[seasonColumnKey] = seasonData.value;
                        }
                    });

                    rows.push(dataRow);
                });
            }
        });

        return { rows, groupsAvailable };
    }, [data, seasons]);
